import React from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { ReactComponent as CloseIcon } from "../assets/icons/close.svg";
import { usePromotedPopupPoll, usePollPopup as usePopup } from "./api/pollsV2";
import { usePollPopup } from "./hooks/usePollPopup";
import "./styles/SurveyPopup.scss";

/**
 * SurveyPopup component displays a promotional popup for surveys
 *
 * Features:
 * - Appears after a specified delay (delaySeconds)
 * - Can be shown at regular intervals (intervalDays)
 * - Limits total display count (maxDisplayCount)
 * - Stores metadata in cookies to track display history
 * - Fetches poll data using usePromotedPopupPoll hook
 *
 * @param {Object} props
 * @param {number} props.delaySeconds - Delay in seconds before showing popup (default: 5)
 * @param {number} props.intervalDays - Days between popup displays (default: 7)
 * @param {number} props.maxDisplayCount - Maximum times to show popup (default: 3)
 * @param {string} props.cookieName - Cookie name for storing metadata (default: "survey_popup_metadata")
 */
function SurveyPopup({
  delaySeconds = 3,
  intervalDays = 7,
  maxDisplayCount = 5,
  cookieName = "survey_popup_metadata",
}) {
  const { i18n, t } = useTranslation(["survey"]);
  const history = useHistory();

  // Get current language code (PL, GB, etc.)
  const langCode = Cookies.get("user_lang")
    ? Cookies.get("user_lang").toUpperCase()
    : i18n.language.toUpperCase();

  // Fetch promoted popup poll
  const { poll, isLoading, isError } = usePromotedPopupPoll(langCode);

  const promotedPopupID = poll
    ? poll.promoted_popup
      ? poll.promoted_popup.id
      : null
    : null;

  const { popup } = usePopup(promotedPopupID);

  // Use poll popup hook for display logic
  const { isOpen, shouldShow, handleClose, handleDismiss } = usePollPopup({
    poll,
    isLoading,
    isError,
    delaySeconds,
    intervalDays,
    maxDisplayCount: poll ? poll.max_view_number : maxDisplayCount,
    cookieName,
  });

  /**
   * Handle CTA click - navigate to poll page
   */
  const handleCtaClick = () => {
    if (poll && poll.slug) {
      history.push(`/ankiety/${poll.slug}`);
      handleClose();
    }
  };

  // Don't render anything if no poll or conditions not met
  if (!poll || !shouldShow || !popup) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: {
          zIndex: "1000",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        },
        content: {
          position: "relative",
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
          padding: 0,
          border: "none",
          borderRadius: 0,
          inset: "auto",
          overflow: "visible",
          backgroundColor: "transparent",
        },
      }}
      className="survey-popup__modal"
    >
      <div className="survey-popup">
        {/* Hero Image */}
        {popup.image && (
          <div className="survey-popup__image">
            <img src={popup.image.file} alt="" />
          </div>
        )}

        {/* Content Container */}
        <div className="survey-popup__content">
          {/* Title */}
          <h4 className="survey-popup__title">{popup.headline}</h4>

          {/* Description */}
          <div className="survey-popup__description-container">
            <div className="survey-popup__description">
              {poll.description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: popup.content,
                  }}
                />
              )}
            </div>
          </div>

          {/* CTA Button */}
          <div className="survey-popup__button-row">
            <button
              type="button"
              className="survey-popup__cta-button"
              onClick={handleCtaClick}
            >
              <span className="survey-popup__cta-text">
                {popup.cta_text
                  ? popup.cta_text
                  : t("survey:popup.cta", "PRZEJDŹ DO ANKIETY")}
              </span>
            </button>
          </div>
        </div>

        {/* Close Button */}
        <button
          type="button"
          className="survey-popup__close-button"
          onClick={handleDismiss}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>
    </Modal>
  );
}

export default SurveyPopup;
